import { ApolloQueryResult } from "@apollo/client";

import {
  isProgramLead,
  isProgramParticipant,
} from "@/components/helpers/workspace/permissions";
import { Box, Flex } from "@/components/layout/flexbox";
import { ProgramDashboardFeed } from "@/components/pages_logged_in/program/components/ProgramDashboardFeed";
import { ProgramDashboardInitComponents } from "@/components/pages_logged_in/program/components/ProgramDashboardInitComponents";
import { ProgramRightPanel } from "@/components/pages_logged_in/program/components/right-panel/ProgramRightPanel";
import { ProgramComponentsQuery, ProgramQuery } from "@/graphql";
import { MainProgram } from "@/types/programs";

import { ProgramLead } from "./right-panel/LeadItem";

export const getExecContact = (
  program: MainProgram,
): ProgramLead | undefined => {
  if (!program?.execContact) {
    return undefined;
  }

  return {
    id: program.execContact.id,
    user: {
      id: program.execContact.id,
      photo: program.execContact.photo,
      name: program.execContact.name,
      email: program.execContact.email,
    },
    summary: program.execContact.jobTitle || "Exec Contact",
  };
};

interface ProgramDashboardProps {
  program: MainProgram;
  componentsData: ProgramComponentsQuery;
  refetchProgram: () => Promise<ApolloQueryResult<ProgramQuery>>;
  refetchProgramComponents: () => Promise<
    ApolloQueryResult<ProgramComponentsQuery>
  >;
}

export function ProgramDashboard({
  program,
  componentsData,
  refetchProgram,
  refetchProgramComponents,
}: ProgramDashboardProps) {
  const hasComponents = program?.hasComponents;
  const isLead = isProgramLead(program?.id);
  const isParticipant = isProgramParticipant(program.id);

  return (
    <Box>
      <ProgramDashboardInitComponents program={program} />

      <Flex
        width="100%"
        flexDirection={["column", "column", "column", "row"]}
        pb={100}
        style={{ position: "relative" }}
      >
        <Box flex={1} mt={36}>
          <ProgramDashboardFeed
            program={program}
            componentsData={componentsData}
            isParticipant={isParticipant}
            isLead={isLead}
            refetchProgramComponents={refetchProgramComponents}
            refetchProgram={refetchProgram}
          />
        </Box>
        <Box
          width={["100%", 275, 275, 275]}
          ml={[0, 0, 0, 3]}
          px={[3, 0, 0, 0]}
        >
          <ProgramRightPanel
            program={program}
            isLead={isLead}
            refetchProgram={refetchProgram}
            quickActions={["addComponent", "addParticipants"]}
            showProgramLeads={true}
            showProgramLeadModification={false}
            showExecLead={isLead}
            execContact={isLead ? getExecContact(program) : undefined}
          />
        </Box>
        {!hasComponents && (
          <Box
            style={{
              position: "absolute",
              display: "flex",
              opacity: 0.7,
              background: "white",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          ></Box>
        )}
      </Flex>
    </Box>
  );
}
